import { useMemo } from "react";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { Kind, Payment } from "@b2bportal/air-booking-api";
import type {
  IPriceLineItem,
  IProductLineItem,
  ISummaryLineItem,
} from "@b2bportal/core-types";
import { SummaryLineType } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import { useI18nContext, useI18nName } from "@hopper-b2b/i18n";

/** RBC default currency when API omits it */
const DEFAULT_CURRENCY_CODE = "CAD";

const KIND_PRODUCT_TYPE_MAP: Partial<Record<Kind, Product>> = {
  [Kind.Cfar]: Product.AirCfar,
  [Kind.Chfar]: Product.AirChfar,
  [Kind.Luggage]: Product.AirBaggage,
  [Kind.Delay]: Product.AirDisruption,
  [Kind.MissedConnection]: Product.AirDisruption,
  [Kind.Insurance]: Product.AirInsurance,
};

export function useFlightTripDetailPriceBreakdown(
  itinerary: BookedFlightItineraryWithDepartureTime
) {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();
  const { formatFullName } = useI18nName();

  return useMemo(() => {
    const { sellingPricing, seats } = itinerary.bookedItinerary;
    const { pricingByPassenger, totalPricing } = sellingPricing;
    const currencyCode =
      totalPricing?.total?.fiat?.currencyCode ??
      pricingByPassenger?.[0]?.currency;

    const paymentLineItems =
      itinerary.bookedItinerary.paymentBreakdown?.paymentLineItems;
    const rewardsPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.RbcAvionPoints
    );
    const cardPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.Card
    );
    const creditPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.TravelWalletCredit
    );

    const numberOfFixedPoints =
      rewardsPayment?.value?.pointsRedemptionBreakdown?.numberOfFixedPoints ??
      0;
    const isGridBooking = numberOfFixedPoints > 0;

    const seatedPassengerCount = isGridBooking
      ? pricingByPassenger.filter((p) => p.passengerType !== "INF").length
      : 0;
    // Grid tiers assign the same fixed points per seated passenger,
    // so numberOfFixedPoints is always evenly divisible by passenger count.
    const fixedPointsPerPassenger =
      seatedPassengerCount > 0
        ? Math.round(numberOfFixedPoints / seatedPassengerCount)
        : 0;

    // Seats are not included in totalPricing.total — add them manually
    // (same pattern as getFlightTotalWithSeats in shared libs)
    const totalSeatCost =
      seats?.seats?.reduce((sum, s) => sum + (s.price ?? 0), 0) ?? 0;

    const priceLineItems: IPriceLineItem[] = pricingByPassenger.map(
      (pricing) => {
        const lineTitle = formatFullName(pricing.person);

        // Ancillary products from pricing (CFAR, baggage, insurance, etc.)
        // Note: Seat charges are NOT included in ancillaries - they come from seats.seats below
        const ancillaryProducts: IProductLineItem[] = (
          pricing.ancillaries ?? []
        ).reduce<IProductLineItem[]>((acc, a) => {
          if (a.premium?.fiat?.value == null || a.premium.fiat.value === 0) {
            return acc;
          }

          const kind = a.kind as Kind;
          const productType = KIND_PRODUCT_TYPE_MAP[kind];

          // Map each known Kind to its translated label
          let label: string;
          switch (kind) {
            case Kind.Cfar:
            case Kind.Delay:
            case Kind.MissedConnection:
            case Kind.Insurance:
              return acc;
            case Kind.Luggage:
              label = t("core-checkout.priceBreakdown.baggage");
              break;
            case Kind.CancelOrChange:
            case Kind.Chfar:
            case Kind.Points:
              label = String(kind);
              break;
            default:
              ensureExhaustive(kind);
          }

          acc.push({
            label,
            value: formatFiatCurrency(a.premium.fiat),
            type: productType ?? (kind as unknown as Product),
          });

          return acc;
        }, []);

        // Seat charges are calculated separately from seats.seats to avoid duplication
        const seatTotal =
          seats?.seats
            ?.filter((s) => s.personId === pricing.person.id)
            .reduce((sum, s) => sum + (s.price ?? 0), 0) ?? 0;
        const seatProduct =
          seatTotal > 0
            ? [
                {
                  label: t("core-checkout.priceBreakdown.seats"),
                  value: formatFiatCurrency({
                    currencyCode: currencyCode ?? DEFAULT_CURRENCY_CODE,
                    value: seatTotal,
                  }),
                  type: Product.Seats,
                },
              ]
            : [];

        const overageProduct =
          (pricing.fixedPointsOverage?.fiat?.value ?? 0) > 0
            ? [
                {
                  label: t("core-checkout.priceBreakdown.fixedPointsOverage"),
                  value: formatFiatCurrency(pricing.fixedPointsOverage.fiat),
                  type: Product.RbcPoints,
                },
              ]
            : [];

        const products = [
          ...overageProduct,
          ...ancillaryProducts,
          ...seatProduct,
        ];

        const isLapInfant = pricing.passengerType === "INF";
        const baseFareValue =
          isGridBooking && !isLapInfant
            ? formatRewardsCurrency(
                {
                  value: fixedPointsPerPassenger,
                  currency: "Points",
                },
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              )
            : formatFiatCurrency(pricing.baseWithoutMargin.fiat);

        return {
          lineTitle,
          baseAmount: {
            label: t("core-checkout.priceBreakdown.baseFare"),
            value: baseFareValue,
          },
          taxesAndFees: {
            label: t("core-checkout.priceBreakdown.taxesFees"),
            value: formatFiatCurrency(pricing.taxes.fiat),
          },
          products,
          taxBreakdown: pricing.taxBreakdown,
        };
      }
    );

    const inclTaxesSubtitle = t("core-checkout.priceBreakdown.inclTaxes");

    const summaryLineItems: ISummaryLineItem[] = [
      ...totalPricing.ancillaries.reduce<ISummaryLineItem[]>(
        (acc, ancillary) => {
          switch (ancillary.kind) {
            case Kind.Cfar:
              acc.push({
                label: t("core-checkout.priceBreakdown.cfar"),
                subtitle: inclTaxesSubtitle,
                value: formatFiatCurrency(ancillary.premium.fiat),
                type: SummaryLineType.FINTECH,
              });
              break;
            case Kind.Delay:
              acc.push({
                label: t("core-checkout.priceBreakdown.disruption"),
                subtitle: inclTaxesSubtitle,
                value: formatFiatCurrency(ancillary.premium.fiat),
                type: SummaryLineType.FINTECH,
              });
              break;
            case Kind.Insurance:
              acc.push({
                label: t("core-checkout.priceBreakdown.travelInsurance"),
                value: formatFiatCurrency(ancillary.premium.fiat),
                type: SummaryLineType.PRODUCT,
              });
              break;
            case Kind.MissedConnection:
            case Kind.Luggage:
            case Kind.CancelOrChange:
            case Kind.Chfar:
            case Kind.Points:
              break;
            default:
              ensureExhaustive(ancillary.kind);
          }
          return acc;
        },
        []
      ),
      {
        label: t("core-checkout.priceBreakdown.total"),
        value: (() => {
          const totalFiatWithSeats =
            (totalPricing.total.fiat?.value ?? 0) + totalSeatCost;

          if (!isGridBooking) {
            return formatFiatCurrency({
              currencyCode: currencyCode ?? DEFAULT_CURRENCY_CODE,
              value: totalFiatWithSeats,
            });
          }
          const pointsTotal = formatRewardsCurrency(
            {
              value: numberOfFixedPoints,
              currency: "Points",
            },
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          );
          if (totalFiatWithSeats > 0) {
            const fiatTotal = formatFiatCurrency({
              currencyCode: currencyCode ?? DEFAULT_CURRENCY_CODE,
              value: totalFiatWithSeats,
            });
            return `${pointsTotal} + ${fiatTotal}`;
          }
          return pointsTotal;
        })(),
        type: SummaryLineType.TOTAL,
      },
    ];

    const creditAmount = creditPayment?.value?.paymentAmount?.amount ?? 0;
    if (creditPayment && creditAmount > 0) {
      summaryLineItems.push({
        label: t("trips.travelCreditsApplied"),
        value: `-${formatFiatCurrency({
          value: creditAmount,
          currencyCode:
            creditPayment.value?.paymentAmount?.currency ??
            currencyCode ??
            DEFAULT_CURRENCY_CODE,
        })}`,
        type: SummaryLineType.PRODUCT,
        className: "travel-credits",
      });
    }

    const numberOfFlexiblePoints =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.numberOfFlexiblePoints ?? 0;

    if (rewardsPayment && numberOfFlexiblePoints > 0) {
      const pointsRedeemed = formatRewardsCurrency(
        {
          value: numberOfFlexiblePoints,
          currency: "Points",
        },
        { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      );

      const flexFiat =
        rewardsPayment.value?.pointsRedemptionBreakdown
          ?.flexiblePointsCashEquivalent;
      const redeemedValue = flexFiat?.value
        ? `-${formatFiatCurrency({
            value: flexFiat.value,
            currencyCode: flexFiat.currencyCode ?? DEFAULT_CURRENCY_CODE,
          })}`
        : "";

      summaryLineItems.push({
        label: t("trips.redeemedPoints", { points: pointsRedeemed }),
        value: redeemedValue,
        type: SummaryLineType.PRODUCT,
        className: "redeemed-points",
      });
    }

    if (
      totalPricing.exchangeFee?.fiat?.value != null &&
      totalPricing.exchangeFee.fiat.value > 0
    ) {
      summaryLineItems.push({
        label: t("trips.exchangeFee"),
        value: formatFiatCurrency(totalPricing.exchangeFee.fiat),
        type: SummaryLineType.PRODUCT,
      });
    }

    const amountDue = formatFiatCurrency({
      value: cardPayment?.value?.paymentAmount?.amount ?? 0,
      currencyCode:
        cardPayment?.value?.paymentAmount?.currency ??
        currencyCode ??
        DEFAULT_CURRENCY_CODE,
    });

    const paymentMethodDisplay = cardPayment?.value?.cardNumberDisplay ?? null;

    return {
      priceLineItems,
      summaryLineItems,
      amountDue,
      paymentMethodDisplay,
      isGridBooking,
      currencyCode: totalPricing.total.fiat?.currencyCode,
    };
  }, [
    itinerary.bookedItinerary,
    t,
    formatFiatCurrency,
    formatRewardsCurrency,
    formatFullName,
  ]);
}
